/**
 * Boards — list of the writer's visual whiteboards (Excalidraw canvases).
 * Standalone boards that can be linked to one or more scripts.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { AppHeader } from "@/components/Header";
import { TrialBanner } from "@/components/TrialBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { Plus, Shapes, Copy, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Boards() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const isCollaborator = user?.account_type === "collaborator";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/boards");
      setBoards(data.boards || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load boards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createBoard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/boards", { title: "Untitled Board" });
      navigate(`/app/boards/${data.board.id}`);
    } catch (e) {
      if (e.response?.status === 402) { navigate("/locked"); return; }
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create board");
    } finally { setBusy(false); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/boards/${id}/duplicate`);
      toast.success(`Duplicated as "${data.board.title}"`);
      load();
    } catch (e) {
      if (e.response?.status === 402) { navigate("/locked"); return; }
      toast.error(formatApiError(e.response?.data?.detail) || "Could not duplicate");
    }
  };

  const remove = async (id, title) => {
    const ok = await confirm({
      title: "Delete this board?",
      description: `"${title}" and everything on it will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete board",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/boards/${id}`);
      setBoards((b) => b.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  if (isCollaborator) {
    return (
      <div className="min-h-screen bg-[var(--canvas)]">
        <AppHeader />
        <main className="max-w-[1400px] mx-auto px-6 py-16 text-center">
          <p className="text-[var(--ink-secondary)]">Boards are available on writer accounts.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <ImpersonationBanner />
      <AppHeader />
      <TrialBanner />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-[var(--ink-primary)]">Boards</h1>
            <p className="text-[var(--ink-secondary)] text-sm mt-1">
              A blank canvas for maps, mood boards, story webs and timelines. Sketch anything, then link it to your scripts.
            </p>
          </div>
          <button onClick={createBoard} disabled={busy} className="btn-brand inline-flex items-center gap-2 shrink-0" data-testid="new-board-btn">
            <Plus size={16} /> New board
          </button>
        </div>

        {loading ? (
          <p className="text-[var(--ink-muted)] text-sm" data-testid="boards-loading">Loading your boards…</p>
        ) : boards.length === 0 ? (
          <div className="border border-dashed border-[var(--line)] rounded-2xl py-20 text-center" data-testid="boards-empty">
            <Shapes size={40} strokeWidth={1.2} className="mx-auto text-[var(--brand)] mb-4" />
            <p className="font-display text-2xl text-[var(--ink-primary)] mb-1">Your first board awaits</p>
            <p className="text-[var(--ink-secondary)] text-sm mb-6">Drop in shapes, notes, images and connections — however your story lives in your head.</p>
            <button onClick={createBoard} disabled={busy} className="btn-brand inline-flex items-center gap-2 mx-auto" data-testid="empty-new-board-btn">
              <Plus size={16} /> Create a board
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="boards-grid">
            <AnimatePresence>
              {boards.map((b) => (
                <motion.div
                  key={b.id}
                  layout
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
                  className="group relative rounded-xl border border-[var(--line)] bg-[var(--paper)] overflow-hidden hover:shadow-lg hover:border-[var(--brand)]/40 transition-all cursor-pointer"
                  onClick={() => navigate(`/app/boards/${b.id}`)}
                  data-testid={`board-card-${b.id}`}
                >
                  <div className="aspect-[16/10] bg-[var(--canvas)] border-b border-[var(--line)] overflow-hidden flex items-center justify-center">
                    {b.thumbnail ? (
                      <img src={b.thumbnail} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Shapes size={30} strokeWidth={1.2} className="text-[var(--ink-muted)]" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-[var(--ink-primary)] truncate" data-testid={`board-title-${b.id}`}>{b.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-[var(--ink-muted)]">
                      <span>{timeAgo(b.updated_at)}</span>
                      {(b.script_ids?.length || 0) > 0 && (
                        <span className="inline-flex items-center gap-1"><Link2 size={11} /> {b.script_ids.length} script{b.script_ids.length > 1 ? "s" : ""}</span>
                      )}
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); duplicate(b.id); }} className="p-1.5 rounded-md bg-[var(--paper)]/90 border border-[var(--line)] text-[var(--ink-secondary)] hover:text-[var(--ink-primary)]" title="Duplicate" data-testid={`board-duplicate-${b.id}`}>
                      <Copy size={13} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(b.id, b.title); }} className="p-1.5 rounded-md bg-[var(--paper)]/90 border border-[var(--line)] text-[var(--ink-secondary)] hover:text-[var(--err)]" title="Delete" data-testid={`board-delete-${b.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}
